import { supabase } from './supabase';
import { parseAnalysisResult } from './analysisParser';
import { trackProductEvent } from './productEvents';

function normalizeOptionalNumber(value) {
  if (
    value === null ||
    value === undefined ||
    value === ''
  ) {
    return null;
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
}

export async function fetchJournalEntries(userId) {
  const { data, error } = await supabase
    .from('journal_entries')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
}

export async function createJournalEntryFromAnalysis({
  userId,
  analysis,
  source = 'analysis',
}) {
  if (!analysis?.id) {
    throw new Error(
      'Analysis ID is required to create a journal entry.'
    );
  }

  const { data: existingEntry, error: existingError } =
    await supabase
      .from('journal_entries')
      .select('*')
      .eq('user_id', userId)
      .eq('analysis_id', analysis.id)
      .maybeSingle();

  if (existingError) throw existingError;

  if (existingEntry) {
    return existingEntry;
  }

  const parsed = parseAnalysisResult(
    analysis.raw_result || ''
  );

  const supportValue =
    parsed.supportReference ||
    parsed.supportZone ||
    '';

  const structuralChangeValue =
    parsed.structuralChangeLevel ||
    parsed.invalidationLevel ||
    '';

  const resistanceValue =
    parsed.resistanceReference ||
    parsed.resistanceLevel ||
    '';

  const featuresSnapshot =
    analysis.features_snapshot ||
    analysis.featuresSnapshot ||
    {};

  const priceAtAnalysis = normalizeOptionalNumber(
    analysis.price_at_analysis ??
      analysis.priceAtAnalysis
  );

  const analysisTimestamp =
    analysis.analysis_timestamp ||
    analysis.analysisTimestamp ||
    analysis.created_at ||
    null;

  const snapshotVersion = normalizeOptionalNumber(
    analysis.snapshot_version ??
      analysis.snapshotVersion
  );

  const researchIntent =
    analysis.research_intent ||
    analysis.researchIntent ||
    null;

  const payload = {
    user_id: userId,
    analysis_id: analysis.id,
    ticker: analysis.ticker,
    title: `${analysis.ticker} research note`,

    technical_conviction:
      parsed.technicalConviction || '',

    support_zone: supportValue,
    invalidation_level: structuralChangeValue,
    resistance_level: resistanceValue,

    ai_explanation:
      parsed.currentStructure ||
      parsed.explanation ||
      '',

    thesis: '',
    setup_notes: '',
    execution_status: 'watching',
    confidence: 3,
    lesson_learned: '',
    outcome_status: 'open',
    followed_plan: 'not_set',

    price_at_analysis: priceAtAnalysis,

    /*
      Phase 0 preserves the analyzer's captured market price in
      the journal. We do not make a second quote API request here.
    */
    price_at_journal_save: priceAtAnalysis,

    analysis_timestamp: analysisTimestamp,
    snapshot_version: snapshotVersion,
    research_intent: researchIntent,

    snapshot: {
      ticker: analysis.ticker,
      raw_result: analysis.raw_result || '',
      parsed,
      analysis_created_at:
        analysis.created_at || null,

      features_snapshot: featuresSnapshot,
      price_at_analysis: priceAtAnalysis,
      analysis_timestamp: analysisTimestamp,
      snapshot_version: snapshotVersion,
      research_intent: researchIntent,
    },
  };

  const { data, error } = await supabase
    .from('journal_entries')
    .insert(payload)
    .select()
    .single();

  if (error) throw error;

  void trackProductEvent({
    userId,
    eventName: 'journal_created',
    analysisId: analysis.id,
    journalEntryId: data.id,
    ticker: data.ticker,
    metadata: {
      source,
      research_intent: researchIntent,
    },
  });

  return data;
}

export async function createBlankJournalEntry({ userId, ticker = '' }) {
  const { data, error } = await supabase
    .from('journal_entries')
    .insert({
      user_id: userId,
      ticker,
      title: ticker ? `${ticker} research note` : 'New research note',
      execution_status: 'watching',
      confidence: 3,
      outcome_status: 'open',
      followed_plan: 'not_set',
      snapshot: {},
    })
    .select()
    .single();

  void trackProductEvent({
    userId,
    eventName: 'journal_created',
    journalEntryId: data.id,
    ticker: data.ticker || null,
    metadata: {
      source: 'blank',
    },
  });

  if (error) throw error;
  return data;
}

export async function updateJournalEntry(entryId, updates) {
  const { data, error } = await supabase
    .from('journal_entries')
    .update(updates)
    .eq('id', entryId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function deleteJournalEntry(entryId) {
  const { error } = await supabase
    .from('journal_entries')
    .delete()
    .eq('id', entryId);

  if (error) throw error;
}

export async function fetchRecentAnalyses(userId) {
  const { data, error } = await supabase
    .from('analyses')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(3);

  if (error) throw error;
  return data || [];
}